import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { initializeApp, getApp, getApps } from "firebase/app";
import { getDatabase, ref, onValue, off } from "firebase/database";
import axios from "axios";
import { useAuth } from "./AuthContext";

const RealtimeContext = createContext(null);

// Firebase config will be loaded from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase only if config is available
let app = null;
let database = null;

try {
  if (firebaseConfig.apiKey && firebaseConfig.databaseURL) {
    app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    database = getDatabase(app);
  }
} catch (error) {
  console.warn("Firebase initialization failed:", error);
}

export const RealtimeProvider = ({ children }) => {
  const { user, isPaired } = useAuth();
  const [realtimeData, setRealtimeData] = useState({
    // Only keep milestones and fantasy in RTDB
    milestones: null,
    fantasy: null,
    lastUpdate: null,
  });

  // Get pair key for the couple
  const getPairKey = useCallback(() => {
    if (!user?.id || !user?.partner_id) return null;
    const ids = [user.id, user.partner_id].sort();
    return `${ids[0]}_${ids[1]}`;
  }, [user]);

  // Subscribe to REAL-TIME updates (RTDB only for milestones and fantasy)
  useEffect(() => {
    if (!database || !isPaired || !user?.id) return;

    const pairKey = getPairKey();
    if (!pairKey) return;

    const activeListeners = [];

    // Listen to milestones updates only (keep in RTDB)
    const milestonesRef = ref(database, `pairs/${pairKey}/milestones`);
    onValue(milestonesRef, (snapshot) => {
      const data = snapshot.val();
      setRealtimeData((prev) => ({
        ...prev,
        milestones: data,
        lastUpdate: Date.now(),
      }));
    });
    activeListeners.push({ ref: milestonesRef });

    // Listen to fantasy matches updates (keep in RTDB)
    const fantasyRef = ref(database, `pairs/${pairKey}/fantasy`);
    onValue(fantasyRef, (snapshot) => {
      const data = snapshot.val();
      setRealtimeData((prev) => ({
        ...prev,
        fantasy: data,
        lastUpdate: Date.now(),
      }));
    });
    activeListeners.push({ ref: fantasyRef });

    // Cleanup function
    return () => {
      activeListeners.forEach(({ ref: listenerRef }) => {
        off(listenerRef);
      });
    };
  }, [database, isPaired, user, getPairKey]);

  const value = {
    ...realtimeData,
    pairKey: getPairKey(),
    isConnected: !!database && isPaired,
    database,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error("useRealtime must be used within a RealtimeProvider");
  }
  return context;
};

// Custom hook for subscribing to specific path
export const useRealtimeValue = (path) => {
  const [value, setValue] = useState(null);
  const { pairKey, isConnected } = useRealtime();

  useEffect(() => {
    if (!database || !isConnected || !path) return;

    const fullPath = path.startsWith("pairs/")
      ? path
      : `pairs/${pairKey}/${path}`;
    const valueRef = ref(database, fullPath);

    const unsubscribe = onValue(valueRef, (snapshot) => {
      setValue(snapshot.val());
    });

    return () => off(valueRef);
  }, [path, pairKey, isConnected]);

  return value;
};
